import { useEffect, useState } from "react";
import { Bell, PackageCheck, Clock, Info } from "lucide-react";
import { api, ApiClientError } from "../lib/api";
import { KpiTile } from "../components/KpiTile";

interface NotificationSummary {
  pendingApprovals: number;
  pendingPickUp: number;
  readyToShip: number;
}

/**
 * FR-OH-003's notification chips (`GET /orders/notification-summary`) are the only real, reachable
 * outbound-notification signal exposed by the API today — there is no route that lists individual
 * rows from the `notifications` outbox table (checked apps/api/src/index.ts and every
 * modules/**\/routes.ts). Rather than fabricate a feed of notification rows, this screen shows the
 * real counts and is explicit that the row-level outbox log isn't reachable yet.
 */
export function Alerts() {
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<NotificationSummary>("/orders/notification-summary")
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiClientError ? err.message : "Unable to load notification activity.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col gap-5">
      {error ? (
        <div className="card p-6 text-center">
          <div className="text-sm text-status-red font-semibold">{error}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <KpiTile icon={Bell} iconColor="orange" label="Pending Approvals" value={loading ? "…" : summary?.pendingApprovals ?? 0} />
          <KpiTile icon={PackageCheck} iconColor="blue" label="Ready to Ship" value={loading ? "…" : summary?.readyToShip ?? 0} />
          <KpiTile icon={Clock} iconColor="purple" label="Pending Pick-Up" value={loading ? "…" : summary?.pendingPickUp ?? 0} />
        </div>
      )}

      <div className="card p-5">
        <h3 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-2.5 flex items-center gap-2">
          <Info size={14} /> About this page
        </h3>
        <p className="text-sm text-ink-muted">
          These counts reflect real order records that need notification-related action (orders pending notification
          approval, ready to ship, or awaiting customer pick-up). A row-level log of individual outbound notification
          attempts (email/SMS/print, sent/failed/retried) is recorded in the platform's notification outbox, but no API
          endpoint currently exposes that list to this screen — so it is not shown here rather than being faked.
        </p>
      </div>
    </div>
  );
}
